import { useState } from 'react';
import { Navigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import api from '../lib/api';
import { toast } from 'sonner';
import PasswordInput from '../components/PasswordInput';
import '@/App.css';

export default function LoginPage() {
  const { user, loading, login } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [forceChangeOpen, setForceChangeOpen] = useState(false);
  const [newPwd, setNewPwd] = useState('');
  const [confirmPwd, setConfirmPwd] = useState('');

  if (loading) return <div className="min-h-screen bg-[#1A1A1A] flex items-center justify-center"><div className="spinner-gold" /></div>;
  if (user && !user.must_change_password) return <Navigate to="/dashboard" />;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSubmitting(true);
    const result = await login(email, password);
    if (!result.success) setError(result.error);
    else if (result.must_change_password) setForceChangeOpen(true);
    setSubmitting(false);
  };

  const handleForceChange = async () => {
    if (!newPwd || newPwd.length < 6) { toast.error('Minimum 6 characters'); return; }
    if (newPwd !== confirmPwd) { toast.error('Passwords do not match'); return; }
    try {
      await api.post('/auth/change-password', { current_password: password, new_password: newPwd });
      toast.success('Password changed. Welcome!');
      setForceChangeOpen(false);
      window.location.href = '/dashboard';
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
  };

  return (
    <div className="min-h-screen bg-[#1A1A1A] flex items-center justify-center px-4" data-testid="login-page">
      <div className="w-full max-w-md">
        <div className="text-center mb-10">
          <img src="/logo-gold.png" alt="AARYA24kt" className="h-14 mx-auto mb-3" />
          <p className="text-xs tracking-[0.3em] uppercase text-[#E5E0D8] font-sans">Product Catalog System</p>
        </div>
        <div className="bg-white rounded-sm p-8 shadow-2xl">
          <h2 className="font-serif text-xl text-[#1A1A1A] mb-6">Sign In</h2>
          {error && <div className="mb-4 p-3 bg-red-50 border border-red-200 text-red-700 text-sm rounded-sm" data-testid="login-error">{error}</div>}
          <form onSubmit={handleSubmit} className="space-y-5">
            <div>
              <Label className="text-xs tracking-[0.15em] uppercase font-semibold text-[#1A1A1A] mb-2 block">Email</Label>
              <Input
                data-testid="login-email-input"
                type="email"
                value={email}
                onChange={e => setEmail(e.target.value)}
                placeholder="Enter your email"
                required
                className="rounded-sm border-[#E5E0D8] focus:ring-[#C5A059] focus:border-[#C5A059]"
              />
            </div>
            <div>
              <Label className="text-xs tracking-[0.15em] uppercase font-semibold text-[#1A1A1A] mb-2 block">Password</Label>
              <PasswordInput
                data-testid="login-password-input"
                value={password}
                onChange={e => setPassword(e.target.value)}
                placeholder="Enter your password"
                required
                className="rounded-sm border-[#E5E0D8] focus:ring-[#C5A059] focus:border-[#C5A059]"
              />
            </div>
            <Button
              data-testid="login-submit-button"
              type="submit"
              disabled={submitting}
              className="w-full bg-[#C5A059] hover:bg-[#B38F48] text-white rounded-sm h-11 text-sm tracking-wide uppercase font-semibold"
            >
              {submitting ? 'Signing in...' : 'Sign In'}
            </Button>
          </form>
        </div>
        <p className="text-center text-[#5C5C5C] text-xs mt-6 tracking-wide">Internal access only</p>
      </div>

      <Dialog open={forceChangeOpen} onOpenChange={() => {}}>
        <DialogContent className="bg-white rounded-sm border-[#E5E0D8] [&>button]:hidden" data-testid="force-change-dialog">
          <DialogHeader><DialogTitle className="font-serif text-lg">Set Your New Password</DialogTitle></DialogHeader>
          <p className="text-sm text-[#5C5C5C]">For security, please change your temporary password.</p>
          <div className="space-y-4 py-2">
            <div><Label className="text-[10px] tracking-[0.15em] uppercase font-semibold text-[#1A1A1A] mb-2 block">New Password</Label>
              <PasswordInput value={newPwd} onChange={e => setNewPwd(e.target.value)} className="rounded-sm border-[#E5E0D8]" data-testid="force-new-pwd" placeholder="Min 6 characters" /></div>
            <div><Label className="text-[10px] tracking-[0.15em] uppercase font-semibold text-[#1A1A1A] mb-2 block">Confirm Password</Label>
              <PasswordInput value={confirmPwd} onChange={e => setConfirmPwd(e.target.value)} className="rounded-sm border-[#E5E0D8]" data-testid="force-confirm-pwd" /></div>
          </div>
          <DialogFooter><Button onClick={handleForceChange} className="bg-[#C5A059] hover:bg-[#B38F48] text-white rounded-sm" data-testid="force-change-btn">Set Password</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
